import type { Balance, CategoryKind } from "../api/types";
import { formatCents } from "../lib/money";
import { kindColor } from "./common";

/** A bucket's balance by kind, or 0 if the child has no such bucket (or no balance yet). */
export function bucketCents(balance: Balance | null, kind: CategoryKind): number {
  return balance?.byCategory.find((b) => b.kind === kind)?.balanceCents ?? 0;
}

function PendingLine({ balance }: { balance: Balance }) {
  if (!Number.isFinite(balance.pendingCents) || balance.pendingCents === 0) {
    return null;
  }
  // Scheduled money is named here rather than folded into the total, so what
  // is shown is always what can actually be spent.
  return (
    <span className="balance-hero__pending">
      {balance.pendingCents > 0 ? "+" : ""}
      {formatCents(balance.pendingCents)} coming later
    </span>
  );
}

/**
 * The headline balance plus the Spend/Save breakdown.
 *
 * Both numbers come straight from the server, which derives them by summing the
 * ledger — the client never adds up transactions itself, so what a child sees
 * cannot drift from what actually happened.
 *
 * Spend and Save are always the big numbers, with the total below and
 * de-emphasized — on both the child's own dashboard and the parent's
 * child-detail screen, since a parent overseeing a kid's money cares about the
 * same two buckets a kid decides about, not just the sum of them.
 */
export function BalanceView({
  balance,
  label = "Total balance",
}: {
  balance: Balance;
  label?: string;
}) {
  const spend = balance.byCategory.find((b) => b.kind === "spend");
  const save = balance.byCategory.find((b) => b.kind === "save");
  const rest = balance.byCategory.filter(
    (b) => b.kind !== "spend" && b.kind !== "save",
  );

  return (
    <div className="stack">
      <div className="buckets buckets--hero">
        {[spend, save]
          .filter((b): b is NonNullable<typeof b> => Boolean(b))
          .map((bucket) => (
            <div
              key={bucket.categoryId ?? bucket.kind}
              className="bucket bucket--hero"
              style={
                {
                  "--bucket-color": kindColor(bucket.kind, bucket.color),
                } as React.CSSProperties
              }
            >
              <span className="bucket__name">{bucket.name}</span>
              <span className="bucket__amount bucket__amount--hero">
                {formatCents(bucket.balanceCents)}
              </span>
            </div>
          ))}
      </div>

      <div className="balance-total">
        <span className="balance-total__label">{label}</span>
        <span className="balance-total__value">
          {formatCents(balance.totalCents)}
        </span>
        <PendingLine balance={balance} />
      </div>

      {rest.length > 0 ? (
        <div className="buckets">
          {rest.map((bucket) => (
            <div
              key={bucket.categoryId ?? "uncategorized"}
              className="bucket"
              style={
                {
                  "--bucket-color": kindColor(bucket.kind, bucket.color),
                } as React.CSSProperties
              }
            >
              <span className="bucket__name">{bucket.name}</span>
              <span className="bucket__amount">
                {formatCents(bucket.balanceCents)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
